import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ReligionReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReport: (reason: string, details?: string) => void;
  profileName: string;
}

const reportReasons = [
  { value: 'disrespectful_behavior', label: 'Disrespectful behavior', icon: '😤' },
  { value: 'faith_misrepresentation', label: 'Misrepresentation of faith', icon: '🎭' },
  { value: 'intent_misrepresentation', label: 'Misrepresentation of intent', icon: '💔' },
  { value: 'harassment', label: 'Harassment', icon: '🚫' },
  { value: 'inappropriate_content', label: 'Inappropriate content', icon: '⚠️' },
];

const ReligionReportModal: React.FC<ReligionReportModalProps> = ({
  isOpen,
  onClose,
  onReport,
  profileName
}) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [details, setDetails] = useState('');
  const [step, setStep] = useState<'reason' | 'details'>('reason');

  const handleSubmit = () => {
    if (!selectedReason) return;
    onReport(selectedReason, details || undefined);
    // Reset state
    setSelectedReason(null);
    setDetails('');
    setStep('reason');
  };

  const handleClose = () => {
    setSelectedReason(null);
    setDetails('');
    setStep('reason');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm rounded-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-bold">
              Report {profileName}
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={handleClose} className="rounded-full">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </DialogHeader>

        {step === 'reason' ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground mb-4">
              Why are you reporting this person?
            </p>
            
            {reportReasons.map((reason) => (
              <button
                key={reason.value}
                onClick={() => {
                  setSelectedReason(reason.value);
                  setStep('details');
                }}
                className="w-full p-3 rounded-xl border border-border hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all text-left flex items-center gap-3"
              >
                <span className="text-xl">{reason.icon}</span>
                <span className="text-sm font-medium">{reason.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-foreground mb-2">
                Selected: {reportReasons.find(r => r.value === selectedReason)?.label}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('reason')}
                className="text-xs text-muted-foreground"
              >
                Change reason
              </Button>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional details (optional)
              </label>
              <Textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Provide any additional context..."
                className="resize-none h-24 rounded-xl"
                maxLength={500}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 rounded-full bg-red-500 hover:bg-red-600 text-white"
              >
                Submit Report
              </Button>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center mt-2">
          Reports are reviewed within 24 hours
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default ReligionReportModal;
